import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, LogOut, Sun, Moon, Heart, Bell, Plus } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useFavorites } from '../context/FavoritesContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [notifications, setNotifications] = useState({
    total: 0, new_orders: 0, new_users: 0,
    pending_orders_total: 0, processing_orders_total: 0, persistent_count: 0,
    low_stock_count: 0, low_stock_products: [],
    recent_orders: [], recent_users: [],
  });
  const { user, isStaff, logout, hasPermission } = useAuth();
  const { getTotalItems } = useCart();
  const { isDark, toggleTheme } = useTheme();
  const { favoriteIds } = useFavorites();
  const { t, lang, changeLang, languages } = useLanguage();
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    if (!isStaff) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications`);
      setNotifications(res.data);
    } catch (_) {
      // Non-critical: notification fetch failure doesn't affect UX
    }
  }, [isStaff]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markRead = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/notifications/mark-read`, {});
      // Reset "new since last check" counters but keep persistent ones
      // (pending orders / low stock) — they stay visible until the work is done.
      setNotifications(prev => ({ ...prev, new_orders: 0, new_users: 0 }));
    } catch (_) { /* Non-critical: mark-read failure doesn't affect UX */ }
  };

  // Badge displays the most pressing number: persistent pending orders if any,
  // else the "new since last check" total. This guarantees a non-zero badge
  // for as long as orders await processing.
  const badgeCount = notifications.pending_orders_total > 0
    ? notifications.pending_orders_total
    : notifications.total;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-xl border-b border-theme shadow-sm" style={{ backgroundColor: 'rgb(var(--surface-rgb)/0.88)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-[color:var(--color-gold)] whitespace-nowrap" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <span style={{ color: 'var(--color-logo-gold)' }}>Ô'</span>Delicieuse <span className="hidden xl:inline text-[10px] font-bold uppercase tracking-[0.25em] text-[color:var(--color-emerald)] align-middle ml-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Wholesale</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-0.5 bg-sand rounded-full p-1 mx-3">
            <Link to="/" className="nav-link text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap" data-testid="nav-home">{t('nav_home')}</Link>
            <Link to="/catalogue" className="nav-link text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap" data-testid="nav-catalogue">{t('nav_catalogue')}</Link>
            <Link to="/precommande" className="nav-link text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap" data-testid="nav-preorder">{t('nav_preorder')}</Link>
            {user && (
              <Link to="/orders" className="nav-link text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap" data-testid="nav-orders">{t('nav_orders')}</Link>
            )}
            {isStaff && (
              <Link to="/admin" className="text-xs font-bold uppercase tracking-wider bg-[color:var(--color-primary)] text-white rounded-full px-3.5 py-2" data-testid="nav-admin">{t('nav_admin')}</Link>
            )}
          </nav>

          {/* Right Side Icons */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            {/* Language Selector */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="p-2 rounded-full hover:bg-sand transition-colors text-sm font-semibold text-muted hover:text-heading"
                data-testid="lang-selector"
              >
                {languages.find(l => l.code === lang)?.label || 'FR'}
              </button>
              {showLangMenu && (
                <div className="absolute right-0 top-full mt-1 bg-[color:var(--color-surface)] border border-white/10 shadow-2xl z-50 min-w-[140px]" data-testid="lang-menu">
                  {languages.map(l => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => { changeLang(l.code); setShowLangMenu(false); }}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                        lang === l.code ? 'text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]' : 'text-zinc-400 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`lang-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-sand transition-colors"
              data-testid="theme-toggle"
              title={isDark ? 'Mode jour' : 'Mode nuit'}
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-[color:var(--color-primary)]" />
              ) : (
                <Moon className="w-5 h-5 text-[color:var(--color-primary)]" />
              )}
            </button>

            {/* POS shortcut — staff with pos permission only (Phase 38) */}
            {isStaff && hasPermission('pos') && (
              <Link
                to="/admin/preparation"
                className="hidden sm:flex items-center gap-1 bg-[color:var(--color-emerald)] text-white rounded-full hover:opacity-90 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-opacity"
                title="Nouvelle commande au comptoir"
                data-testid="header-pos-shortcut"
              >
                <Plus className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">POS</span>
              </Link>
            )}

            {/* Notifications - Staff only */}
            {isStaff && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setShowNotifPanel(!showNotifPanel); if (!showNotifPanel) markRead(); }}
                  className="relative p-2 rounded-full hover:bg-sand transition-colors"
                  data-testid="header-notifications"
                >
                  <Bell className="w-5 h-5 text-muted hover:text-heading transition-colors" />
                  {badgeCount > 0 && (
                    <span
                      className={`absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 text-white text-xs flex items-center justify-center rounded-full ${
                        notifications.pending_orders_total > 0
                          ? 'bg-red-500 animate-pulse'
                          : 'bg-[color:var(--color-primary)]'
                      }`}
                      data-testid="notif-badge"
                    >
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </button>

                {showNotifPanel && (
                  <div
                    className="fixed sm:absolute inset-x-2 top-[4.5rem] sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80 max-w-[calc(100vw-1rem)] bg-[color:var(--color-surface)] border border-white/10 shadow-2xl z-50 max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col"
                    data-testid="notification-panel"
                  >
                    <div className="p-3 border-b border-white/10 flex items-center justify-between flex-shrink-0">
                      <h4 className="text-white text-sm font-medium">Notifications</h4>
                      <button
                        type="button"
                        onClick={() => setShowNotifPanel(false)}
                        className="sm:hidden text-zinc-400 hover:text-white p-1 -mr-1"
                        aria-label="Fermer"
                        data-testid="notif-panel-close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="overflow-y-auto flex-1 min-h-0">
                      {notifications.recent_orders.length === 0 && notifications.recent_users.length === 0 && !notifications.featured_stale && !notifications.credit_stale && notifications.pending_orders_total === 0 && notifications.low_stock_count === 0 ? (
                        <p className="text-zinc-500 text-sm p-4 text-center">Aucune notification</p>
                      ) : (
                        <>
                          {notifications.pending_orders_total > 0 && (
                            <Link
                              to="/admin/commandes"
                              onClick={() => setShowNotifPanel(false)}
                              data-testid="notif-pending-orders"
                              className="block p-3 border-b border-white/5 bg-red-500/5 hover:bg-red-500/10 transition-colors"
                            >
                              <div className="flex items-start gap-2 min-w-0">
                                <span className="w-2 h-2 bg-red-400 rounded-full flex-shrink-0 animate-pulse mt-1.5" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-white text-sm font-medium break-words">
                                    {notifications.pending_orders_total} commande(s) à traiter
                                  </p>
                                  <p className="text-zinc-500 text-xs break-words">
                                    {notifications.processing_orders_total > 0
                                      ? `+ ${notifications.processing_orders_total} en préparation — cliquez pour les voir`
                                      : 'Cliquez pour les préparer'}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          )}
                          {notifications.low_stock_count > 0 && (
                            <Link
                              to="/admin/produits?filter=low_stock"
                              onClick={() => setShowNotifPanel(false)}
                              data-testid="notif-low-stock"
                              className="block p-3 border-b border-white/5 bg-amber-500/5 hover:bg-amber-500/10 transition-colors"
                            >
                              <div className="flex items-start gap-2 min-w-0">
                                <span className="w-2 h-2 bg-amber-400 rounded-full flex-shrink-0 mt-1.5" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-white text-sm font-medium break-words">
                                    {notifications.low_stock_count} produit(s) en stock bas
                                  </p>
                                  <p className="text-zinc-500 text-xs break-words">
                                    {notifications.low_stock_products
                                      .slice(0, 3)
                                      .map((p) => `${p.name} (${p.stock})`)
                                      .join(' · ')}
                                    {notifications.low_stock_count > 3 ? '…' : ''}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          )}
                          {notifications.credit_stale && (
                            <Link
                              to="/admin/comptabilite"
                              onClick={async () => {
                                setShowNotifPanel(false);
                                try {
                                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                                  await axios.post(
                                    `${API_URL}/api/admin/credit/customers/ack-stale-notification`
                                  );
                                } catch (e) { /* non-blocking */ }
                              }}
                              data-testid="notif-credit-stale"
                              className="block p-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-red-400 rounded-full flex-shrink-0" />
                                <div>
                                  <p className="text-white text-sm">
                                    Dettes clients en attente
                                  </p>
                                  <p className="text-zinc-500 text-xs">
                                    Au moins une dette de plus de 15 jours — pensez à relancer vos clients
                                  </p>
                                </div>
                              </div>
                            </Link>
                          )}
                          {notifications.featured_stale && (
                            <Link
                              to="/admin/coups-de-coeur"
                              onClick={() => setShowNotifPanel(false)}
                              data-testid="notif-featured-stale"
                              className="block p-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-pink-400 rounded-full flex-shrink-0" />
                                <div>
                                  <p className="text-white text-sm">
                                    {notifications.featured_count > 0
                                      ? `Coups de cœur : ${notifications.featured_days_old} jours sans changement`
                                      : 'Aucune sélection coups de cœur active'}
                                  </p>
                                  <p className="text-zinc-500 text-xs">
                                    {notifications.featured_count > 0
                                      ? 'Pensez à actualiser la sélection ou conservez-la'
                                      : 'Mettez en avant 6 à 12 produits sur l\'accueil'}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          )}
                          {notifications.recent_users.map(u => (
                            <Link
                              key={u.id}
                              to="/admin/utilisateurs?filter=pending"
                              onClick={() => setShowNotifPanel(false)}
                              className="block p-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-orange-400 rounded-full flex-shrink-0" />
                                <div>
                                  <p className="text-white text-sm">{u.name} s'est inscrit</p>
                                  <p className="text-zinc-500 text-xs">{u.phone}</p>
                                </div>
                              </div>
                            </Link>
                          ))}
                          {notifications.recent_orders.map(o => (
                            <Link
                              key={o.id}
                              to="/admin/commandes"
                              onClick={() => setShowNotifPanel(false)}
                              className="block p-3 border-b border-white/5 hover:bg-white/5 transition-colors"
                            >
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-[color:var(--color-primary)] rounded-full flex-shrink-0" />
                                <div>
                                  <p className="text-white text-sm">Commande de {o.customer_name}</p>
                                  <p className="text-zinc-500 text-xs">{o.total.toFixed(2)}€</p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Favorites */}
            <Link 
              to="/favoris" 
              className="relative p-2 rounded-full hover:bg-sand transition-colors"
              data-testid="header-favorites"
            >
              <Heart className="w-5 h-5 text-muted hover:text-heading transition-colors" />
              {favoriteIds.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center rounded-full">
                  {favoriteIds.length}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link 
              to="/panier" 
              className="relative p-2 rounded-full hover:bg-sand transition-colors"
              data-testid="header-cart"
            >
              <ShoppingBag className="w-5 h-5 text-muted hover:text-heading transition-colors" />
              {getTotalItems() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[color:var(--color-primary)] text-white text-xs font-bold flex items-center justify-center">
                  {getTotalItems()}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <div className="flex items-center gap-1">
                <Link 
                  to="/compte" 
                  className="hidden lg:flex items-center gap-2 text-muted hover:text-heading transition-colors p-2"
                  data-testid="header-account"
                >
                  <User className="w-5 h-5" />
                  <span className="text-sm hidden xl:inline">{user.name}</span>
                  {user.pricing_tier && (
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                        user.pricing_tier === 'vip'
                          ? 'text-[color:var(--color-primary)] border-[color:rgb(var(--primary-rgb)/0.50)] bg-[color:rgb(var(--primary-rgb)/0.05)]'
                          : user.pricing_tier === 'gros'
                            ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/5'
                            : 'text-blue-300 border-blue-500/40 bg-blue-500/5'
                      }`}
                      data-testid="header-tier-badge"
                      title={
                        user.pricing_tier === 'vip'
                          ? 'Tarif VIP appliqué à toutes vos commandes'
                          : user.pricing_tier === 'gros'
                            ? 'Tarif Gros appliqué à toutes vos commandes'
                            : 'Tarif Semi-gros appliqué à toutes vos commandes'
                      }
                    >
                      {user.pricing_tier === 'vip' ? '✨ VIP' : user.pricing_tier === 'gros' ? 'Gros' : 'S-Gros'}
                    </span>
                  )}
                </Link>
                <button 
                  onClick={handleLogout}
                  className="hidden lg:block p-2 rounded-full hover:bg-sand transition-colors"
                  data-testid="header-logout"
                  title={t('nav_logout')}
                >
                  <LogOut className="w-5 h-5 text-muted hover:text-heading transition-colors" />
                </button>
              </div>
            ) : (
              <Link 
                to="/connexion" 
                className="hidden lg:flex items-center gap-2 text-muted hover:text-heading transition-colors p-2"
                data-testid="header-login"
              >
                <User className="w-5 h-5" />
                <span className="text-sm uppercase tracking-wider">{t('nav_login')}</span>
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 rounded-full hover:bg-sand transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-heading" />
              ) : (
                <Menu className="w-6 h-6 text-heading" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-theme py-4">
            <nav className="flex flex-col gap-3">
              <Link to="/" className="nav-link py-2 text-sm uppercase tracking-widest" onClick={() => setMobileMenuOpen(false)}>{t('nav_home')}</Link>
              <Link to="/catalogue" className="nav-link py-2 text-sm uppercase tracking-widest" onClick={() => setMobileMenuOpen(false)}>{t('nav_catalogue')}</Link>
              <Link to="/precommande" className="nav-link py-2 text-sm uppercase tracking-widest" onClick={() => setMobileMenuOpen(false)} data-testid="nav-preorder-mobile">{t('nav_preorder')}</Link>
              <Link to="/favoris" className="nav-link py-2 text-sm uppercase tracking-widest flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                <Heart className="w-4 h-4" /> {t('nav_favorites')}
                {favoriteIds.length > 0 && <span className="text-xs text-red-500">({favoriteIds.length})</span>}
              </Link>
              {user && (
                <Link to="/orders" className="nav-link py-2 text-sm uppercase tracking-widest" onClick={() => setMobileMenuOpen(false)}>{t('nav_orders')}</Link>
              )}
              {isStaff && (
                <Link to="/admin" className="nav-link py-2 text-sm uppercase tracking-widest font-bold text-[color:var(--color-gold)]" onClick={() => setMobileMenuOpen(false)}>{t('nav_admin')}</Link>
              )}
              {isStaff && hasPermission('pos') && (
                <Link to="/admin/preparation" className="nav-link py-2 text-sm uppercase tracking-widest font-bold text-[color:var(--color-emerald)] flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="nav-pos-mobile"><Plus className="w-4 h-4" /> POS</Link>
              )}
              {user ? (
                <>
                  <Link to="/compte" className="nav-link py-2 text-sm uppercase tracking-widest" onClick={() => setMobileMenuOpen(false)}>{t('nav_my_account')}</Link>
                  <button onClick={handleLogout} className="nav-link py-2 text-left text-sm uppercase tracking-widest">{t('nav_logout')}</button>
                </>
              ) : (
                <Link to="/connexion" className="nav-link py-2 text-sm uppercase tracking-widest" onClick={() => setMobileMenuOpen(false)}>{t('nav_login')}</Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
